import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@acast-tech/auth-client';
import { useNavigate } from 'react-router-dom';
import { config } from '@/config';
import { fetchGraphQL } from '@/services/graphqlClient';

const VIEWER_QUERY = `
  query Viewer {
    viewer {
      id: databaseId
      email
      roles {
        role {
          id
          name
          permissions {
            app
            scope
            action
            resource
          }
        }
        scopes {
          networkIds
          showIds
        }
      }
    }
  }
`;

type Permission = {
  app: string;
  scope: string;
  action: string;
  resource: string;
};

type UserRole = {
  role: {
    id: string;
    name: string;
    permissions: Permission[];
  };
  scopes: {
    networkIds: string[] | null;
    showIds: string[] | null;
  };
};

export type UserRolesResponse = {
  viewer: {
    id: string;
    email: string;
    roles: UserRole[];
  } | null;
};

const fetchUserRoles = (token: string): Promise<UserRolesResponse> =>
  fetchGraphQL<UserRolesResponse>({ url: config.graphqlApiUrl, token, query: VIEWER_QUERY });

/**
 * Hook to fetch the authenticated user's roles directly from the GraphQL API
 * @returns The user's roles data via React Query
 */
export const useUserRoles = () => {
  const { getToken, logout } = useAuth();
  const navigate = useNavigate();

  return useQuery({
    queryKey: ['user-roles'],
    queryFn: async () => {
      try {
        const token = await getToken();
        return await fetchUserRoles(token);
      } catch (err) {
        if (err instanceof Error && err.message.includes('401')) {
          logout();
          navigate('/');
          throw new Error('Authentication failed');
        }
        throw err;
      }
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - matches the cache TTL in roles-loader
  });
};
